import { useMemo } from "react";
import type { ApexOptions } from "apexcharts";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { SearchCoverageChartProps } from "./SearchCoverageChart";
import { CHART_DATE_TICK_AMOUNT, formatChartDateAxisLabel } from "../../../utils/chart";

const roundToOneDecimal = (val: number): number => Math.round(val * 10) / 10;

const formatPercent = (val: number): string => `${val.toFixed(1)}%`;

export const useSearchCoverageChartController = ({ metric }: SearchCoverageChartProps) => {
  const i18n = useI18n();
  const averageCoverage = Math.round(metric.average.value);

  const categories = useMemo(
    () => metric.timeSeries.dataPoints.map((dp) => new Date(dp.time).getTime()),
    [metric.timeSeries.dataPoints]
  );

  const yAxisMax = useMemo(() => {
    const peak = metric.timeSeries.dataPoints.reduce((acc, dp) => Math.max(acc, dp.value), 0);
    if (peak <= 0) {
      return 100;
    }
    // Add 20% headroom above the peak and round up the result, then cap at 100%.
    const withHeadroom = peak * 1.2;
    const step = withHeadroom <= 10 ? 1 : withHeadroom <= 50 ? 5 : 10;
    return Math.min(100, Math.ceil(withHeadroom / step) * step);
  }, [metric.timeSeries.dataPoints]);

  const series: ApexAxisChartSeries = useMemo(
    () => [
      {
        name: i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_data_key")),
        data: metric.timeSeries.dataPoints.map((dp) => roundToOneDecimal(dp.value)),
      },
    ],
    [metric.timeSeries.dataPoints, i18n]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          columnWidth: "60%",
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: ["var(--bui_color_action_foreground)"],
      xaxis: {
        type: "category",
        categories,
        tickPlacement: "on",
        tickAmount: CHART_DATE_TICK_AMOUNT,
        title: {
          text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_xaxis")),
          offsetY: 24,
        },
        labels: {
          rotate: 0,
          rotateAlways: false,
          formatter: formatChartDateAxisLabel,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        labels: {
          formatter: formatPercent,
          padding: 40,
        },
      },
      grid: {
        borderColor: "var(--bui_color_border_alt)",
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 24, left: 32, right: 24, bottom: 20 },
      },
      tooltip: {
        x: { format: "d MMM yyyy" },
        y: {
          title: {
            formatter: () =>
              i18n
                .trans(
                  t("flights_airline_portal_realtime_performance_metric_three_data_point_label", {
                    variables: { percent: "" },
                  })
                )
                .replace(": %", ":"),
          },
          formatter: formatPercent,
        },
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: "top",
        horizontalAlign: "right",
        markers: { size: 7, shape: "circle" },
        offsetY: 0,
        onItemClick: { toggleDataSeries: false },
      },
    }),
    [categories, i18n, yAxisMax]
  );

  return {
    averageCoverage,
    options,
    series,
  };
};
